import React, { useState, useRef, useEffect } from 'react';
import './EmbeddedCardView.less';
import {
  getBrandColor,
  isLocalizeAdded,
  LocalizePropType,
  withTextLocalizer,
} from 'getaway-data-layer';
import { format, isAfter, isSameDay } from 'date-fns';
import { GA_TITLES, isMobileBrowser } from '../../../../utils';
import BrandButton from '../../../components/BrandButton/BrandButton';
import { useQuery } from '../../../../hooks/useQuery';
import { DateRange } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { de } from 'date-fns/locale';

type Props = {
  localize: LocalizePropType;
  selectedStartDate: Date;
  onChangeStartDate: (date: Date) => void;
  selectedEndDate: Date;
  onChangeEndDate: (date: Date) => void;
  minStart: Date;
  minEnd: Date;
  maxEnd: Date;
  onReset: () => void;
  onPressFilter: () => void;
  customError: string;
};

const EmbeddedCardView = ({
  localize,
  selectedStartDate,
  onChangeStartDate,
  selectedEndDate,
  onChangeEndDate,
  minStart,
  minEnd,
  maxEnd,
  onReset,
  onPressFilter,
  customError,
}: Props) => {
  const query = useQuery();

  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showStartTimePicker, setShowStartTimePicker] = useState(false);
  const [showEndTimePicker, setShowEndTimePicker] = useState(false);
  const startTimeRef = useRef(null);
  const endTimeRef = useRef(null);
  const startTimeDropdownRef = useRef(null);
  const endTimeDropdownRef = useRef(null);
  const datePickerRef = useRef(null);
  const embeddedCardRef = useRef(null);

  const cardRect = embeddedCardRef?.current?.getBoundingClientRect();
  const calendarTop = cardRect?.height - 50;

  const [dateRange, setDateRange] = useState({
    startDate: selectedStartDate,
    endDate: selectedEndDate,
    key: 'selection',
  });

  // Pre-generate time options in 15-minute intervals (24-hour format)
  const timeOptions = React.useMemo(() => {
    const options = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        const formattedHour = hour.toString().padStart(2, '0');
        const formattedMinute = minute.toString().padStart(2, '0');
        options.push(`${formattedHour}:${formattedMinute}`);
      }
    }
    return options;
  }, []);

  useEffect(() => {
    if (showStartTimePicker && startTimeDropdownRef.current) {
      const currentTimeStr = format(selectedStartDate, 'HH:mm');
      const selectedIndex = timeOptions.findIndex(
        (time) => time === currentTimeStr
      );
      if (selectedIndex > -1) {
        const optionHeight = 36; // Approximate height of each time option
        startTimeDropdownRef.current.scrollTop = selectedIndex * optionHeight;
      }
    }
  }, [showStartTimePicker, selectedStartDate, timeOptions]);

  useEffect(() => {
    if (showEndTimePicker && endTimeDropdownRef.current) {
      const currentTimeStr = format(selectedEndDate, 'HH:mm');
      const selectedIndex = timeOptions.findIndex(
        (time) => time === currentTimeStr
      );
      if (selectedIndex > -1) {
        const optionHeight = 36; // Approximate height of each time option
        endTimeDropdownRef.current.scrollTop = selectedIndex * optionHeight;
      }
    }
  }, [showEndTimePicker, selectedEndDate, timeOptions]);

  const handleDateRangeChange = (ranges) => {
    const { startDate, endDate } = ranges.selection;

    // Keep the time part from the original dates
    const newStartDate = new Date(startDate);
    newStartDate.setHours(
      selectedStartDate.getHours(),
      selectedStartDate.getMinutes(),
      0,
      0
    );

    const newEndDate = new Date(endDate);
    newEndDate.setHours(
      selectedEndDate.getHours(),
      selectedEndDate.getMinutes(),
      0,
      0
    );

    setDateRange({
      startDate: newStartDate,
      endDate: newEndDate,
      key: 'selection',
    });

    onChangeStartDate(newStartDate);
    onChangeEndDate(newEndDate);
  };

  const handleStartTimeChange = (timeString) => {
    const [hours, minutes] = timeString.split(':').map(Number);
    const newDate = new Date(selectedStartDate);
    newDate.setHours(hours, minutes, 0, 0);
    onChangeStartDate(newDate);
    setShowStartTimePicker(false);
  };

  const handleEndTimeChange = (timeString) => {
    const [hours, minutes] = timeString.split(':').map(Number);
    const newDate = new Date(selectedEndDate);
    newDate.setHours(hours, minutes, 0, 0);
    onChangeEndDate(newDate);
    setShowEndTimePicker(false);
  };

  // Effect to handle outside clicks
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        startTimeRef.current &&
        !startTimeRef.current.contains(event.target)
      ) {
        setShowStartTimePicker(false);
      }
      if (endTimeRef.current && !endTimeRef.current.contains(event.target)) {
        setShowEndTimePicker(false);
      }
      if (isMobileBrowser()) {
        if (
          datePickerRef.current &&
          !datePickerRef.current.contains(event.target) &&
          !event.target.closest('.embedded-card-date-display-box')
        ) {
          setShowDatePicker(false);
        }
      }
      if (!isMobileBrowser()) {
        if (
          datePickerRef.current &&
          !datePickerRef.current.contains(event.target) &&
          !event.target.closest('.embedded-card-date-value')
        ) {
          setShowDatePicker(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    setDateRange({
      startDate: selectedStartDate,
      endDate: selectedEndDate,
      key: 'selection',
    });
  }, [selectedStartDate, selectedEndDate]);

  // Check if a time should be disabled for start time
  const isStartTimeDisabled = (time) => {
    // Create a date object with the time string
    const [hours, minutes] = time.split(':').map(Number);
    const timeDate = new Date(selectedStartDate);
    timeDate.setHours(hours, minutes, 0, 0);

    // Check if the time is before minStart
    return isAfter(minStart, timeDate);
  };

  // Check if a time should be disabled for end time
  const isEndTimeDisabled = (time) => {
    const [hours, minutes] = time.split(':').map(Number);
    const timeDate = new Date(selectedEndDate);
    timeDate.setHours(hours, minutes, 0, 0);

    // Check if the time is before minEnd or after maxEnd
    const isBeforeMinEnd = isAfter(minEnd, timeDate);
    const isAfterMaxEnd = maxEnd ? isAfter(timeDate, maxEnd) : false;

    // If on the same day, also check if end time is at or before start time
    let isBeforeOrEqualStartTime = false;
    if (isSameDay(selectedStartDate, selectedEndDate)) {
      const [startHours, startMinutes] = format(selectedStartDate, 'HH:mm')
        .split(':')
        .map(Number);

      // Check if end time is before or same as start time
      isBeforeOrEqualStartTime =
        hours < startHours || (hours === startHours && minutes <= startMinutes);
    }

    return isBeforeMinEnd || isAfterMaxEnd || isBeforeOrEqualStartTime;
  };

  return (
    <div className="embedded-card" ref={embeddedCardRef}>
      {isLocalizeAdded(localize, 'map.embedded.card.title') && (
        <div className="embedded-card-title">
          {localize('map.embedded.card.title')}
        </div>
      )}
      <div className="embedded-card-content">
        <div className="embedded-card-inputs">
          <div className="embedded-card-date-picker-wrapper">
            <div className="embedded-card-date-picker-section">
              <div className="embedded-card-date-label">
                {localize('map.embedded.from')}
              </div>
              <div
                className="embedded-card-date-display-box"
                onClick={() => setShowDatePicker(!showDatePicker)}
                data-testid="EmbeddedCardView-pickupDate"
              >
                <div className="embedded-card-date-value">
                  {format(selectedStartDate, 'EEE, d MMM', {
                    locale: de,
                  }).replace('.,', ',')}
                </div>
                <div
                  className="embedded-card-time-value"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowStartTimePicker(!showStartTimePicker);
                    setShowEndTimePicker(false);
                  }}
                  ref={startTimeRef}
                >
                  {format(selectedStartDate, 'HH:mm', { locale: de })}
                  {showStartTimePicker && (
                    <div
                      className="embedded-card-time-dropdown"
                      ref={startTimeDropdownRef}
                    >
                      {timeOptions.map((time) => (
                        <div
                          key={time}
                          className={`embedded-card-time-option ${
                            time === format(selectedStartDate, 'HH:mm')
                              ? 'selected'
                              : ''
                          } ${isStartTimeDisabled(time) ? 'disabled' : ''}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!isStartTimeDisabled(time)) {
                              handleStartTimeChange(time);
                            }
                          }}
                        >
                          {time}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="embedded-card-date-picker-section">
              <div className="date-label">{localize('map.embedded.to')}</div>
              <div
                className="embedded-card-date-display-box"
                onClick={() => setShowDatePicker(!showDatePicker)}
              >
                <div className="embedded-card-date-value">
                  {format(selectedEndDate, 'EEE, d MMM', {
                    locale: de,
                  }).replace('.,', ',')}
                </div>
                <div
                  className="embedded-card-time-value"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowEndTimePicker(!showEndTimePicker);
                    setShowStartTimePicker(false);
                  }}
                  ref={endTimeRef}
                >
                  {format(selectedEndDate, 'HH:mm', { locale: de })}
                  {showEndTimePicker && (
                    <div
                      className="embedded-card-time-dropdown"
                      ref={endTimeDropdownRef}
                    >
                      {timeOptions.map((time) => (
                        <div
                          key={time}
                          className={`embedded-card-time-option ${
                            time === format(selectedEndDate, 'HH:mm')
                              ? 'selected'
                              : ''
                          } ${isEndTimeDisabled(time) ? 'disabled' : ''}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!isEndTimeDisabled(time)) {
                              handleEndTimeChange(time);
                            }
                          }}
                        >
                          {time}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Calendar (shown/hidden) */}
        {showDatePicker && (
          <div
            className="embedded-card-calendar-container"
            ref={datePickerRef}
            style={!isMobileBrowser() ? { top: calendarTop } : {}}
          >
            <DateRange
              ranges={[dateRange]}
              onChange={handleDateRangeChange}
              minDate={minStart}
              maxDate={maxEnd}
              months={isMobileBrowser() ? 4 : 3}
              direction={isMobileBrowser() ? 'vertical' : 'horizontal'}
              showMonthAndYearPickers={false}
              showDateDisplay={false}
              rangeColors={[getBrandColor()]}
              locale={de}
              scroll={{ enabled: isMobileBrowser() }}
              preventSnapRefocus={!isMobileBrowser()}
            />
            {isMobileBrowser() && (
              <div
                className="close-calendar-btn"
                onClick={() => setShowDatePicker(false)}
              >
                {localize('calendar.dismiss')}
              </div>
            )}
          </div>
        )}

        <div className="embedded-card-button">
          {isMobileBrowser() && (
            <div className="embedded-card-reset" onClick={onReset}>
              {localize('map.filter.reset')}
            </div>
          )}
          <BrandButton
            id={GA_TITLES.BOOKING.SET_FILTER}
            containerStyles={{
              width: isMobileBrowser() ? '200px' : '220px',
              position: 'unset',
              left: 'unset',
              bottom: 'unset',
              fontSize: '15px',
              borderRadius: '12px',
              boxShadow: '0px 0px 21px -5px rgba(0,0,0,0.3)',
              ...(query.get('buttonBg') && {
                backgroundColor: `#${query.get('buttonBg')}`,
              }),
            }}
            title={localize('map.embedded.card.button')}
            disabled={!!customError}
            onClick={onPressFilter}
            enableKeyboardEffect={false}
          />
        </div>
        {isMobileBrowser() && customError && (
          <div className="embedded-card-error">{customError}</div>
        )}
      </div>
      {!isMobileBrowser() && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
          }}
        >
          <div className="embedded-card-reset" onClick={onReset}>
            {localize('map.filter.reset')}
          </div>

          {customError && (
            <div className="embedded-card-error">{customError}</div>
          )}
        </div>
      )}
    </div>
  );
};

export default withTextLocalizer(EmbeddedCardView);
